import React, { useRef, useState } from "react";
import { ArrowLeft, Upload, Loader2, Save, ImageIcon, X } from "lucide-react";
import api, { mediaUrl } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";

export default function BlogEditor({ post, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: post?.title || "",
    excerpt: post?.excerpt || "",
    category: post?.category || "General",
    tags: (post?.tags || []).join(", "),
    author: post?.author || "Elite Vending Team",
    body: post?.body || "",
    heroImage: post?.image || "",
    heroImageAlt: post?.heroImageAlt || "",
    metaTitle: post?.metaTitle || "",
    metaDescription: post?.metaDescription || "",
    published: post?.published !== undefined ? post.published : true,
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const { toast } = useToast();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const fieldCls = "h-11 rounded-lg border-gold/30 focus-visible:ring-gold bg-white";

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, heroImage: res.data.url, heroImageAlt: f.heroImageAlt || f.title }));
      toast({ title: "Image uploaded" });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || "", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast({ title: "Title is required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (post?.id) {
        await api.put(`/admin/blog/${post.id}`, payload);
      } else {
        await api.post("/admin/blog", payload);
      }
      toast({ title: post?.id ? "Article updated" : "Article published" });
      onSaved();
    } catch (err) {
      toast({ title: "Save failed", description: err?.response?.data?.detail || "", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <button onClick={onClose} className="flex items-center gap-2 text-sm font-semibold text-ink hover:text-gold-dark">
          <ArrowLeft size={16} /> Back to articles
        </button>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 rounded-full bg-ink hover:bg-gold hover:text-ink text-white px-6 h-11 text-sm font-semibold transition-colors">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {post?.id ? "Update" : "Publish"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-4">
          <div className="space-y-1.5">
            <Label>Blog Title</Label>
            <Input value={form.title} onChange={set("title")} placeholder="An attention-grabbing headline" className={fieldCls} />
          </div>
          <div className="space-y-1.5">
            <Label>Excerpt / Summary</Label>
            <Textarea value={form.excerpt} onChange={set("excerpt")} placeholder="One or two sentences shown on cards and previews" className="rounded-lg border-gold/30 focus-visible:ring-gold bg-white min-h-[70px]" />
          </div>
          <div className="space-y-1.5">
            <Label>Blog Content</Label>
            <Textarea value={form.body} onChange={set("body")} placeholder={"Write your article here...\n\nStart a line with ## for a heading.\nStart a line with > for a quote.\nLeave a blank line between paragraphs."} className="rounded-lg border-gold/30 focus-visible:ring-gold bg-white min-h-[340px] font-mono text-sm" />
            <p className="text-xs text-muted-foreground">Formatting: <code className="text-gold-dark">## Heading</code>, <code className="text-gold-dark">&gt; Quote</code>, blank line = new paragraph.</p>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          {/* Hero image */}
          <div className="rounded-xl border border-gold/20 p-4">
            <Label className="flex items-center gap-2"><ImageIcon size={15} className="text-gold-dark" /> Hero Image</Label>
            <div className="mt-3 aspect-video rounded-lg overflow-hidden bg-gold-soft grid place-items-center relative">
              {form.heroImage ? (
                <>
                  <img src={mediaUrl(form.heroImage)} alt={form.heroImageAlt} className="h-full w-full object-cover" />
                  <button onClick={() => setForm((f) => ({ ...f, heroImage: "" }))} className="absolute top-2 right-2 grid place-items-center h-7 w-7 rounded-full bg-ink/70 text-white hover:bg-red-500"><X size={14} /></button>
                </>
              ) : (
                <span className="text-sm text-muted-foreground">No image yet</span>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleUpload} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="mt-3 w-full flex items-center justify-center gap-2 rounded-full border border-gold text-ink hover:bg-gold px-4 h-10 text-sm font-semibold transition-colors">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {form.heroImage ? "Replace image" : "Upload image"}
            </button>
            <div className="mt-3 space-y-1.5">
              <Label className="text-xs">Image alt text (SEO)</Label>
              <Input value={form.heroImageAlt} onChange={set("heroImageAlt")} placeholder="Describe the image for search & accessibility" className="h-10 rounded-lg border-gold/30 focus-visible:ring-gold bg-white text-sm" />
            </div>
          </div>

          {/* Taxonomy */}
          <div className="rounded-xl border border-gold/20 p-4 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <Input value={form.category} onChange={set("category")} placeholder="e.g. Workplace" className="h-10 rounded-lg border-gold/30 focus-visible:ring-gold bg-white text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tags (comma separated)</Label>
              <Input value={form.tags} onChange={set("tags")} placeholder="snacks, wellbeing, office" className="h-10 rounded-lg border-gold/30 focus-visible:ring-gold bg-white text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Author</Label>
              <Input value={form.author} onChange={set("author")} className="h-10 rounded-lg border-gold/30 focus-visible:ring-gold bg-white text-sm" />
            </div>
          </div>

          {/* SEO */}
          <div className="rounded-xl border border-gold/20 p-4 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-gold-dark">SEO</p>
            <div className="space-y-1.5">
              <Label className="text-xs">Meta Title</Label>
              <Input value={form.metaTitle} onChange={set("metaTitle")} placeholder="Defaults to blog title" className="h-10 rounded-lg border-gold/30 focus-visible:ring-gold bg-white text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Meta Description</Label>
              <Textarea value={form.metaDescription} onChange={set("metaDescription")} placeholder="Defaults to excerpt" className="rounded-lg border-gold/30 focus-visible:ring-gold bg-white min-h-[70px] text-sm" />
            </div>
          </div>

          {/* Publish toggle */}
          <div className="rounded-xl border border-gold/20 p-4 flex items-center justify-between">
            <div>
              <p className="font-semibold text-ink text-sm">Published</p>
              <p className="text-xs text-muted-foreground">Live on the public blog</p>
            </div>
            <Switch checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} />
          </div>
        </div>
      </div>
    </div>
  );
}
